/**
 * Front and back matter generated from the book's metadata (decision 16): the title page, copyright page,
 * dedication and epigraph before the chapters; acknowledgements, about the author and other books after
 * them. Part 05 log, table F. The pages are plain text in roles, never markup: the preview, EPUB, PDF and
 * DOCX each set the roles with the theme, and escape the text for their format, so nothing a writer puts in
 * their metadata can become markup or code (decision 44).
 */
import type { BookLanguage, BookMetadata } from './metadata.ts';
import { frozen } from './record.ts';

/** What a matter element is, which each output styles in its own way. */
export type MatterRole =
  | 'title'
  | 'subtitle'
  | 'author'
  | 'publisher'
  | 'copyright'
  | 'rights'
  | 'isbn'
  | 'heading'
  | 'paragraph'
  | 'source'
  | 'item';

/** One element: its lines, each a non-empty run of text without line breaks or surrounding space. */
export interface MatterElement {
  readonly role: MatterRole;
  readonly lines: readonly string[];
}

/** A generated page's kind. */
export type MatterKind = 'title' | 'copyright' | 'dedication' | 'epigraph' | 'acknowledgements' | 'about-author' | 'also-by';

/** One generated page (in EPUB, one document; in DOCX and the PDF, starting on a new page). */
export interface MatterPage {
  readonly kind: MatterKind;
  readonly elements: readonly MatterElement[];
}

/** The pages before the first chapter and after the last. */
export interface BookMatter {
  readonly front: readonly MatterPage[];
  readonly back: readonly MatterPage[];
}

/**
 * The words the templates add, in the book's language (decision 33), never the UI's (F12). The ISBN line
 * ("ISBN 978…") and the epigraph's source line ("— source") are the same in every language.
 */
export interface MatterWords {
  /** The copyright notice from the year (or null) and the holder (or blank); never both missing. */
  readonly copyright: (year: number | null, holder: string) => string;
  readonly rightsReserved: string;
  readonly acknowledgements: string;
  /** The "about the author" heading from the author (or blank). */
  readonly aboutAuthor: (author: string) => string;
  /** The "other books" heading from the author (or blank). */
  readonly alsoBy: (author: string) => string;
  /** The contents' heading (part 20, S5: the EPUB's navigation document). */
  readonly contents: string;
  /** The Manuscript title page's word count (part 22, MS6) from the count, already written in the language's digits. */
  readonly wordCount: (count: string) => string;
}

/** A notice of the form "<mark> <year> <holder>", leaving out what is missing. */
const notice =
  (mark: string) =>
  (year: number | null, holder: string): string =>
    [mark, year === null ? '' : String(year), holder].filter((part) => part !== '').join(' ');

/** A heading: the same whatever the author, or one naming the author and one for when none is set. */
type Heading = string | readonly [named: (author: string) => string, unnamed: string];

const heading = (value: Heading, author: string): string =>
  typeof value === 'string' ? value : author === '' ? value[1] : value[0](author);

/** One language's words. */
const words = (mark: string, rightsReserved: string, acknowledgements: string, about: Heading, also: Heading, contents: string, count: readonly [before: string, after: string]): MatterWords => ({
  copyright: notice(mark),
  rightsReserved,
  acknowledgements,
  aboutAuthor: (author) => heading(about, author),
  alsoBy: (author) => heading(also, author),
  contents,
  wordCount: (n) => `${count[0]}${n}${count[1]}`,
});

/**
 * The template words for every book language (F12). English is the publishing trade's own; the others are
 * the usual trade phrases, and a native speaker should check them before launch (part 05 log). Where naming
 * the author would need an inflected form, the heading leaves the name out.
 */
export const MATTER_WORDS: Readonly<Record<BookLanguage, MatterWords>> = frozen({
  'en-US': words('Copyright ©', 'All rights reserved.', 'Acknowledgments', 'About the Author', [(a) => `Also by ${a}`, 'Also by the Author'], 'Contents', ['about ', ' words']),
  'en-GB': words('Copyright ©', 'All rights reserved.', 'Acknowledgements', 'About the Author', [(a) => `Also by ${a}`, 'Also by the Author'], 'Contents', ['about ', ' words']),
  de: words('©', 'Alle Rechte vorbehalten.', 'Danksagung', [(a) => `Über ${a}`, 'Über den Autor'], [(a) => `Weitere Titel von ${a}`, 'Weitere Titel'], 'Inhalt', ['ca. ', ' Wörter']),
  fr: words('©', 'Tous droits réservés.', 'Remerciements', "À propos de l'auteur", 'Du même auteur', 'Table des matières', ['environ ', ' mots']),
  es: words('©', 'Todos los derechos reservados.', 'Agradecimientos', 'Sobre el autor', [(a) => `Otras obras de ${a}`, 'Otras obras del autor'], 'Índice', ['unas ', ' palabras']),
  it: words('©', 'Tutti i diritti riservati.', 'Ringraziamenti', "Nota sull'autore", 'Dello stesso autore', 'Indice', ['circa ', ' parole']),
  pt: words('©', 'Todos os direitos reservados.', 'Agradecimentos', 'Sobre o autor', [(a) => `Outras obras de ${a}`, 'Outras obras do autor'], 'Índice', ['cerca de ', ' palavras']),
  nl: words('©', 'Alle rechten voorbehouden.', 'Dankwoord', [(a) => `Over ${a}`, 'Over de auteur'], [(a) => `Ook van ${a}`, 'Ook van deze auteur'], 'Inhoud', ['ongeveer ', ' woorden']),
  sv: words('©', 'Alla rättigheter förbehållna.', 'Tack', [(a) => `Om ${a}`, 'Om författaren'], 'Av samma författare', 'Innehåll', ['cirka ', ' ord']),
  nb: words('©', 'Alle rettigheter forbeholdt.', 'Takk', [(a) => `Om ${a}`, 'Om forfatteren'], 'Av samme forfatter', 'Innhold', ['ca. ', ' ord']),
  nn: words('©', 'Alle rettar reserverte.', 'Takk', [(a) => `Om ${a}`, 'Om forfattaren'], 'Av same forfattar', 'Innhald', ['ca. ', ' ord']),
  da: words('©', 'Alle rettigheder forbeholdes.', 'Tak', [(a) => `Om ${a}`, 'Om forfatteren'], 'Af samme forfatter', 'Indhold', ['ca. ', ' ord']),
  fi: words('©', 'Kaikki oikeudet pidätetään.', 'Kiitokset', 'Kirjailijasta', 'Samalta kirjailijalta', 'Sisällys', ['noin ', ' sanaa']),
  pl: words('©', 'Wszelkie prawa zastrzeżone.', 'Podziękowania', 'O autorze', 'Tego samego autora', 'Spis treści', ['około ', ' słów']),
  cs: words('©', 'Všechna práva vyhrazena.', 'Poděkování', 'O autorovi', 'Od téhož autora', 'Obsah', ['přibližně ', ' slov']),
  ca: words('©', 'Tots els drets reservats.', 'Agraïments', "Sobre l'autor", 'Del mateix autor', 'Índex', ['unes ', ' paraules']),
});

/** A one-line field with surrounding space removed; blank becomes empty. */
const clean = (value: string): string => value.trim();

/**
 * Free text split into paragraphs (F9): lines are trimmed, a run of blank lines separates paragraphs, and
 * blank lines at either end are dropped. Each paragraph keeps its line breaks (a dedication may be verse).
 */
export function paragraphs(text: string): string[][] {
  const out: string[][] = [];
  let current: string[] = [];
  for (const raw of text.split('\n')) {
    const lineText = raw.trim();
    if (lineText !== '') {
      current.push(lineText);
      continue;
    }
    if (current.length > 0) out.push(current);
    current = [];
  }
  if (current.length > 0) out.push(current);
  return out;
}

const element = (role: MatterRole, value: string): MatterElement[] => (value === '' ? [] : [{ role, lines: [value] }]);

const prose = (text: string): MatterElement[] => paragraphs(text).map((lines) => ({ role: 'paragraph', lines }));

/** A page of `elements`, or none when they are empty. */
const page = (kind: MatterKind, elements: readonly MatterElement[]): MatterPage[] =>
  elements.length === 0 ? [] : [{ kind, elements }];

/** A page with a heading over `body`, or none when the body is empty (F6–F8). */
const headed = (kind: MatterKind, heading: string, body: readonly MatterElement[]): MatterPage[] =>
  body.length === 0 ? [] : [{ kind, elements: [{ role: 'heading', lines: [heading] }, ...body] }];

/** The copyright page's elements (F2–F4). */
function copyrightElements(metadata: BookMetadata, words: MatterWords): MatterElement[] {
  const holder = clean(metadata.copyrightHolder) || clean(metadata.author);
  const year = metadata.copyrightYear;
  const text = holder === '' && year === null ? '' : words.copyright(year, holder);
  return [
    ...element('copyright', text),
    ...element('rights', text === '' ? '' : words.rightsReserved),
    ...element('isbn', metadata.isbn === '' ? '' : `ISBN ${metadata.isbn}`),
  ];
}

/**
 * The generated front and back matter (table F). F1: the title page is always there (a title is never
 * blank). Every other page is there only when its fields hold something, in the order of {@link MatterKind}.
 * The words come from the book's language.
 */
export function bookMatter(metadata: BookMetadata): BookMatter {
  const words = MATTER_WORDS[metadata.language];
  const epigraph = prose(metadata.epigraph);
  const source = clean(metadata.epigraphSource);
  const front = [
    ...page('title', [
      ...element('title', clean(metadata.title)),
      ...element('subtitle', clean(metadata.subtitle)),
      ...element('author', clean(metadata.author)),
      ...element('publisher', clean(metadata.publisher)),
    ]),
    ...page('copyright', copyrightElements(metadata, words)),
    ...page('dedication', prose(metadata.dedication)),
    ...page('epigraph', epigraph.length === 0 ? [] : [...epigraph, ...element('source', source === '' ? '' : `— ${source}`)]),
  ];
  const titles = paragraphs(metadata.alsoBy).flat().map((title): MatterElement => ({ role: 'item', lines: [title] }));
  const back = [
    ...headed('acknowledgements', words.acknowledgements, prose(metadata.acknowledgements)),
    ...headed('about-author', words.aboutAuthor(clean(metadata.author)), prose(metadata.aboutAuthor)),
    ...headed('also-by', words.alsoBy(clean(metadata.author)), titles),
  ];
  return { front, back };
}
